import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './store';

interface TimeState {
  isRunning: boolean;
  startTimestamp: number;
  expiryTimestamp: number;
  currentTimestamp: number;
  tock: boolean;
  duration: number;
}

const initialState: TimeState = {
  isRunning: false,
  startTimestamp: -1,
  expiryTimestamp: -1,
  currentTimestamp: Date.now(),
  tock: false,
  duration: 2 * 60 * 60 * 1000,
};

export const timeSlice = createSlice({
  name: 'time',
  initialState,
  reducers: {
    startTimer: (state) => {
      state.isRunning = true;
      const now = Date.now();
      state.currentTimestamp = now;
      state.startTimestamp = now;
      state.expiryTimestamp = now + state.duration;
    },
    stopTimer: (state) => {
      state.isRunning = false;
      state.startTimestamp = -1;
      state.expiryTimestamp = -1;
    },
    tick: (state) => {
      state.currentTimestamp = Date.now();
      state.tock = !state.tock;
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
  },
});

export const { startTimer, stopTimer, tick, setDuration } = timeSlice.actions;

export const selectIsRunning = (state: RootState) => state.time.isRunning;
export const selectStartTimestamp = (state: RootState) => state.time.startTimestamp;
export const selectExpiryTimestamp = (state: RootState) => state.time.expiryTimestamp;
export const selectCurrentTimestamp = (state: RootState) => state.time.currentTimestamp;
export const selectSecondsLeft = (state: RootState) =>
  Math.round((state.time.expiryTimestamp - state.time.currentTimestamp) / 1000);
export const selectIsExpired = (state: RootState) =>
  state.time.currentTimestamp >= state.time.expiryTimestamp;
export const selectTock = (state: RootState) => state.time.tock;
export const selectProgress = (state: RootState) => {
  if (!state.time.isRunning || selectIsExpired(state)) return 0;
  return (
    (100 * (state.time.currentTimestamp - state.time.startTimestamp)) /
    (state.time.expiryTimestamp - state.time.startTimestamp)
  );
};

export default timeSlice.reducer;
